import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useNear } from '@/features/location/store'
import { api } from '@/lib/api'
import { qk } from '@/lib/queryKeys'
import type {
  Paginated,
  ProductCard,
  ProductDetail,
  ProductListResponse,
  ProductSort,
} from '@/types/api'

export const PAGE_SIZE = 24

/** Next page number from a DRF page, for useInfiniteQuery. */
export const nextPage = <T>(last: Paginated<T>, pages: Paginated<T>[]) =>
  last.next ? pages.length + 1 : undefined

export interface ProductListParams {
  town?: string | null
  category?: string | null
  /** A sub-category slug: one chip of the category page (D-81). */
  subcategory?: string | null
  sort?: ProductSort
}

/**
 * GET /products/ — Home "More to explore" and category grids; listed products only (BR-1). With a
 * shared location, nearby shops' products come first (BR-28).
 */
export function useProducts(params: ProductListParams) {
  const near = useNear()
  return useInfiniteQuery({
    queryKey: qk.products({ ...params, near }),
    queryFn: ({ pageParam, signal }) =>
      api<Paginated<ProductCard>>('/products/', {
        query: { ...params, near, page: pageParam, page_size: PAGE_SIZE },
        signal,
      }),
    initialPageParam: 1,
    getNextPageParam: nextPage,
  })
}

/** Query options for GET /products/{id}/ (shared by the page and the cart's barcode fill). */
export const productQuery = (id: number) => ({
  queryKey: qk.product(id),
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    api<ProductDetail>(`/products/${id}/`, { signal }),
})

/** GET /products/{id}/ — 404 when the shop isn't public (BR-1). */
export const useProduct = (id: number) => useQuery({ ...productQuery(id), enabled: id > 0 })

/** GET /products/{id}/related/ — "People also viewed" on the product page (D-93). */
export function useRelated(id: number) {
  return useQuery({
    queryKey: qk.related(id),
    queryFn: ({ signal }) => api<ProductListResponse>(`/products/${id}/related/`, { signal }),
    enabled: id > 0,
    staleTime: 5 * 60_000,
  })
}

/** GET /products/for-you/?ids= — Home "Picked for you" from this device's recently viewed
 * products (ids only; kept on the device, D-93). Off without any. */
export function useForYou(ids: number[], town: string | null) {
  const key = ids.slice(0, 10).join(',')
  return useQuery({
    queryKey: qk.forYou(key, town),
    queryFn: ({ signal }) =>
      api<ProductListResponse>('/products/for-you/', { query: { ids: key, town }, signal }),
    enabled: key.length > 0,
    staleTime: 5 * 60_000,
  })
}
